/* daisy-wrap-kits.js — gift wrap kit colours: one card, a row of colour
 * swatches (the best match for the page pre-selected), plus the half-price
 * second kit.
 *
 * Loaded by snippets/dm-wrap-kits.liquid only while the Classic kit and at
 * least one other colour are active, published and purchasable. Each colour
 * drops out on its own when its product is set to Draft.
 *
 * It layers the colour choice onto every place a builder already offers the
 * classic Gift Wrap Kit, without editing the builders:
 *
 *  A) dm-cyg cards — Mr & Mrs (snippets/dm-gc.liquid) and the pebble-picture
 *     builder (assets/daisy-pebble-picture.js). A swatch row is added above
 *     the card's No thanks / Add buttons. Choosing a colour rewrites the
 *     card's data-variant / data-price, which is exactly what those builders
 *     read at add-to-cart. Tapping a swatch while the kit is off also adds it.
 *
 *  B) tick-box rows in the street-sign clones (input name matches gift-wrap /
 *     giftwrap) and C) "Add a Gift Wrap Kit" extras in the heart / diffuser
 *     builders ([data-heart-extra]): the row is hidden and replaced with the
 *     same card as A, kept in sync with the hidden tick box so the builder's
 *     own submit code still adds the kit. Those builders keep the classic
 *     variant id in their own closures, so the chosen colour is swapped in at
 *     submit time by the hooks below.
 *
 *  Second kit (DAISY_WRAP_KITS.secondKit): once a kit is chosen, a line offers
 *  a second kit at half price in any colour. It is added as its own basket
 *  line; the price cut itself comes from the store's automatic "Second gift
 *  wrap kit half price" discount, so the basket is always the source of truth.
 *  Every colour offered here must be in that discount.
 *
 *  Hooks: DaisyCartSubmit.create().add, DaisyNativeStreetSizes.submit/add and
 *  window.fetch for direct /cart/add.js posts (JSON, form-encoded, FormData).
 *  Every hook is a pure function of page state: nothing changes unless a
 *  colour other than Classic or a second kit was chosen on this page.
 */
(function () {
  'use strict';

  var CFG = window.DAISY_WRAP_KITS;
  if (!CFG || !CFG.classic) return;
  var CLASSIC = CFG.classic;
  var classicId = Number(CLASSIC.variantId);
  if (!classicId) return;

  // Colours in swatch order. Classic first, then Christmas (in season), then the extras.
  var KITS = {};
  var ORDER = [];
  function addKit(k) {
    if (!k || !k.style || !Number(k.variantId) || KITS[k.style]) return;
    if (k.style !== 'classic' && Number(k.variantId) === classicId) return;
    KITS[k.style] = k;
    ORDER.push(k.style);
  }
  addKit(CLASSIC);
  addKit(CFG.christmas);
  (Array.isArray(CFG.extra) ? CFG.extra : []).forEach(addKit);
  if (ORDER.length < 2) return;

  // The classic "Two Kits" variant, which the diffuser builders send when the
  // second-diffuser offer is on. Other colours have no two-pack, so choosing
  // one turns it into 2 x that colour's single kit.
  var twoPackId = Number(CLASSIC.twoPackVariantId) || 0;
  var SECOND = !!CFG.secondKit;
  var SECOND_LABEL = 'Second gift wrap kit (half price)';
  var SECOND_HINT = 'Tap a colour to add it.';

  // Best match for this page, from the product handle in the URL.
  var PATH = String(window.location.pathname || '').toLowerCase();
  function pathHas(words) { return new RegExp('(^|[-/])(' + words + ')([-/]|$)').test(PATH); }
  function bestMatch() {
    if (KITS.christmas && /christmas|xmas|santa|elf|festive|stocking|reindeer|sleigh|wonderland|merry|noel|advent|bauble/.test(PATH)) return 'christmas';
    if (KITS.blush && pathHas('mum|mums|mother|mothers|mummy|mam|nan|nanny|nana|nanna|grandma|gran|sister|galentine|galentines|valentine|valentines|bridesmaid|daughter|auntie|aunt|girl')) return 'blush';
    if (KITS.midnight && pathHas('dad|dads|daddy|father|fathers|grandad|grandads|grandpa|husband|brother|son|uncle|him|groom|boyfriend|men|mens')) return 'midnight';
    if (KITS.emerald && pathHas('home|house|new|kitchen|garden|engagement|proposal|family|tree')) return 'emerald';
    // No obvious match: nothing is pre-selected, the customer picks. (Ivory is
    // only the fallback if they tap "Add gift wrap kit" without choosing.)
    return null;
  }
  var SUGGESTED = bestMatch();
  // Swatch order: the page's best match first, the rest in registry order.
  var SHOWN = SUGGESTED ? [SUGGESTED].concat(ORDER.filter(function (s) { return s !== SUGGESTED; })) : ORDER.slice();

  var state = { style: SUGGESTED || 'classic', rows: 0, second: null };

  function qs(root, sel) { return (root || document).querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function kit(style) { return KITS[style] || CLASSIC; }
  function isKitId(id) {
    id = Number(id);
    if (twoPackId && id === twoPackId) return true;
    return ORDER.some(function (s) { return Number(KITS[s].variantId) === id; });
  }
  function isClassicId(id) { id = Number(id); return id === classicId || (twoPackId && id === twoPackId); }
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(pence) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: CFG.currency || 'GBP' }).format((Number(pence) || 0) / 100);
    } catch (e) {
      return '£' + ((Number(pence) || 0) / 100).toFixed(2);
    }
  }
  function halfPrice(k) { return Math.round(k.price / 2); }
  function pairPrice(k) { return k.price + halfPrice(k); }
  // Write-only-if-different helpers. The card observer below re-syncs on
  // attribute changes, and a no-op classList.toggle / setAttribute still
  // queues a mutation record, so every write here must be conditional.
  function setClass(el, name, on) { if (el.classList.contains(name) !== !!on) el.classList.toggle(name, !!on); }
  function setAttr(el, name, value) { if (el.getAttribute(name) !== value) el.setAttribute(name, value); }
  function setHidden(el, hidden) { if (el.hidden !== !!hidden) el.hidden = !!hidden; }

  /* ---------------- second kit (shared) ---------------- */

  function secondHtml() {
    if (!SECOND) return '';
    return '<div class="dm-wrap-second" hidden>' +
      '<p class="dm-wrap-second__title"><span class="dm-wrap-second__tag">Half price</span> Wrapping another gift? Add a second kit for just <s>' + esc(money(CLASSIC.price)) + '</s> <strong>' + esc(money(halfPrice(CLASSIC))) + '</strong>, any colour.</p>' +
      '<p class="dm-wrap-second__hint" data-wrap-second-hint aria-live="polite">' + esc(SECOND_HINT) + '</p>' +
      '<div class="dm-wrap-second__chips" style="--dm-wrap-n:' + SHOWN.length + '">' + SHOWN.map(secondChip).join('') + '</div></div>';
  }
  function secondChip(style) {
    var k = kit(style);
    return '<button type="button" class="dm-wrap-chip" data-wrap-second="' + esc(style) + '" aria-pressed="false">' +
      '<img src="' + esc(k.thumb) + '" alt="" width="24" height="24" loading="lazy">' + esc(k.label) + '</button>';
  }
  function syncSecond(container, on) {
    var block = qs(container, '.dm-wrap-second');
    if (!block) return;
    if (!on && state.second) state.second = null;
    setHidden(block, !on);
    qsa(block, '[data-wrap-second]').forEach(function (btn) {
      var sel = on && btn.getAttribute('data-wrap-second') === state.second;
      setClass(btn, 'is-selected', sel);
      setAttr(btn, 'aria-pressed', sel ? 'true' : 'false');
    });
    var added = !!(on && state.second && KITS[state.second]);
    setClass(block, 'is-added', added);
    var hint = qs(block, '[data-wrap-second-hint]');
    if (hint) {
      var text = added ? '\u2713 Second kit added: ' + kit(state.second).label + ' for ' + money(halfPrice(kit(state.second))) + '. Tap it again to remove.' : SECOND_HINT;
      if (hint.textContent !== text) hint.textContent = text;
      setClass(hint, 'is-added', added);
    }
    schedulePaint();
  }

  /* ---------------- page totals ----------------
   * The builders total up their own cards, but the second kit only exists as
   * an extra basket line (see the header), so no builder counts it. Add it to
   * whatever total the page shows: the Mr & Mrs summary gets its own row, and
   * every total gets the half price on top. That covers the Mr & Mrs summary
   * and sticky bar, every street-sign / heart / diffuser builder
   * (.daisy-street-options__total), the pebble picture and the teacher pebble.
   * Each is a price-only element. Each total remembers the builder's own
   * figure, so when the builder recalculates we start again from its number,
   * never from ours. */
  var TOTALS = '#dm-cyg-total, .dm-sticky-total__current, .daisy-street-options__total, [data-heart-total], [data-pebble-total], [data-teacher-pebble-total]';
  var paintQueued = false;
  var painting = false;
  function secondExtra() {
    return (SECOND && state.second && KITS[state.second]) ? halfPrice(kit(state.second)) : 0;
  }
  function parsePence(text) {
    var n = parseFloat(String(text || '').replace(/[^\d.]/g, ''));
    return isNaN(n) ? null : Math.round(n * 100);
  }
  function paintTotal(el, extra) {
    var text = el.textContent;
    if (el.getAttribute('data-wrap-shown') !== text) {
      var base = parsePence(text);
      if (base === null) return;
      el.setAttribute('data-wrap-base', String(base));
    }
    var want = money(Number(el.getAttribute('data-wrap-base')) + extra);
    if (text !== want) el.textContent = want;
    setAttr(el, 'data-wrap-shown', want);
  }
  function paintRow(lines, extra) {
    var row = qs(lines, '.dm-wrap-second-row');
    if (!extra) { if (row) row.parentNode.removeChild(row); return; }
    var label = 'Second gift wrap kit \u00b7 ' + kit(state.second).label + ' (half price)';
    var key = label + '|' + extra;
    if (!row) {
      row = document.createElement('div');
      row.className = 'dm-cyg__subtotal-row dm-wrap-second-row';
      lines.appendChild(row);
    } else if (row !== lines.lastElementChild) {
      lines.appendChild(row);
    }
    if (row.getAttribute('data-key') !== key) {
      row.innerHTML = '<span class="dm-cyg__subtotal-label">' + esc(label) + '</span><span class="dm-cyg__subtotal-value">' + esc(money(extra)) + '</span>';
      row.setAttribute('data-key', key);
    }
  }
  function watchTotal(el) {
    if (el.__dmWrapWatched || !window.MutationObserver) return;
    el.__dmWrapWatched = true;
    // Repaint straight from the observer (a microtask, so before the screen
    // redraws): a builder rewriting its total never shows the old figure.
    new MutationObserver(function () { if (!painting) paintTotals(); }).observe(el, { childList: true, characterData: true, subtree: true });
  }
  function paintTotals() {
    paintQueued = false;
    if (painting) return;
    painting = true;
    try {
      var extra = secondExtra();
      var lines = qs(document, '#dm-cyg-subtotal-lines');
      if (lines) { watchTotal(lines); paintRow(lines, extra); }
      qsa(document, TOTALS).forEach(function (el) { watchTotal(el); paintTotal(el, extra); });
    } finally {
      painting = false;
    }
  }
  function schedulePaint() {
    if (paintQueued) return;
    paintQueued = true;
    Promise.resolve().then(paintTotals);
  }
  // The Mr & Mrs summary is drawn from the cards' data-name / data-price, but
  // it only redraws when the sign panel changes. After a colour pick, ask it
  // to redraw so the summary names the colour chosen, not the default kit.
  function redrawBuilder() {
    var panel = qs(document, '[data-daisy-street-options]');
    if (panel) panel.dispatchEvent(new Event('change'));
    schedulePaint();
  }
  function syncAllSecond() {
    qsa(document, '.dm-cyg__card--wrap-styles').forEach(function (card) { syncCygCard(card); });
  }
  document.addEventListener('click', function (event) {
    var chip = event.target && event.target.closest && event.target.closest('[data-wrap-second]');
    if (!chip) return;
    event.preventDefault();
    event.stopPropagation();
    var style = chip.getAttribute('data-wrap-second');
    state.second = state.second === style ? null : style;
    syncAllSecond();
    redrawBuilder();
  }, true);

  /* ---------------- A) dm-cyg cards ---------------- */

  function swatchesHtml() {
    return '<div class="dm-wrap-swatches" role="group" aria-label="Gift wrap colour" style="--dm-wrap-n:' + SHOWN.length + '">' +
      SHOWN.map(function (style) {
        var k = kit(style);
        var badge = style === SUGGESTED ? '<span class="dm-wrap-swatch__badge">Best match</span>' : '';
        return '<button type="button" class="dm-wrap-swatch" data-wrap-pick="' + esc(style) + '" aria-pressed="false">' + badge +
          '<span class="dm-wrap-swatch__img"><img src="' + esc(k.thumb) + '" alt="' + esc(k.name) + '" width="64" height="64" loading="lazy">' +
          '<span class="dm-wrap-swatch__zoom" data-wrap-zoom="' + esc(style) + '" aria-hidden="true"></span></span>' +
          '<span class="dm-wrap-swatch__label">' + esc(k.label) + '</span></button>';
      }).join('') + '</div>';
  }

  function enhanceCygCard(card) {
    if (card.dataset.wrapEnhanced || Number(card.dataset.variant) !== classicId) return;
    var controls = qs(card, '.dm-addon-mode');
    var single = controls && qs(controls, '[data-dm-addon-mode="single"]');
    var media = qs(card, '.dm-cyg__media');
    var body = media && qs(media, '.dm-cyg__body');
    if (!controls || !single || !media || !body) return;
    card.dataset.wrapEnhanced = 'true';
    card.classList.add('dm-cyg__card--wrap-styles');

    var desc = qs(body, '.dm-cyg__desc, .dm-cyg__note');
    if (!desc) {
      desc = document.createElement('p');
      desc.className = 'dm-cyg__desc';
      body.appendChild(desc);
    }
    if (CFG.copy) desc.textContent = CFG.copy;

    var photo = qs(media, 'img.dm-cyg__thumb');
    if (photo) {
      photo.setAttribute('tabindex', '0');
      photo.setAttribute('role', 'button');
      photo.setAttribute('aria-label', 'Look closer at the gift wrap colours');
    }

    var name = qs(body, '.dm-cyg__name');
    if (name && !name.dataset.wrapBaseName) name.dataset.wrapBaseName = name.textContent.trim();

    var row = document.createElement('div');
    row.innerHTML = swatchesHtml();
    controls.parentNode.insertBefore(row.firstChild, controls);

    if (SECOND) {
      var holder = document.createElement('div');
      holder.innerHTML = secondHtml();
      controls.parentNode.insertBefore(holder.firstChild, controls.nextSibling);
    }

    // Price shown on the card follows the colour about to be added.
    applyCygStyle(card, state.style, true);
    syncCygCard(card);
    watchCard(card);
  }

  function applyCygStyle(card, style, quiet) {
    var k = kit(style);
    card.dataset.variant = String(k.variantId);
    card.dataset.price = String(k.price);
    if (card.hasAttribute('data-pair-variant')) card.dataset.pairVariant = String(k.variantId);
    if (card.hasAttribute('data-pair-price')) card.dataset.pairPrice = String(k.price * 2);
    // Mr & Mrs keys its card on data-addon-key, so its data-name (the cart
    // "Add-on" property) can follow the kit. The pebble builder keys on
    // data-name="Gift Wrap Kit" for its second-frame logic, so leave it alone.
    if (card.hasAttribute('data-addon-key')) card.dataset.name = k.name;
    if (!quiet) { card.dataset.wrapStyle = style; state.picked = true; }
    state.style = style;
    var thumb = qs(card, 'img.dm-cyg__thumb');
    if (thumb && k.thumb && thumb.getAttribute('src') !== k.thumb) {
      thumb.src = k.thumb;
      thumb.alt = k.name;
      if (k.zoom) thumb.setAttribute('data-dm-zoom-src', k.zoom);
    }
    var price = qs(card, '.dm-cyg__price');
    if (price) price.textContent = money(k.price);
    var dbl = qs(card, '[data-dm-addon-mode="double"]');
    if (dbl && /£/.test(dbl.textContent)) {
      // Two of the same kit: the second is half price through the automatic discount.
      dbl.textContent = dbl.textContent.replace(/£\s?[\d.,]+/, money(SECOND ? pairPrice(k) : k.price * 2));
    }
  }

  function syncCygCard(card) {
    var on = (card.dataset.addonMode || 'none') !== 'none';
    var style = card.dataset.wrapStyle || (on || SUGGESTED || state.picked ? state.style : '');
    qsa(card, '[data-wrap-pick]').forEach(function (btn) {
      var mine = btn.getAttribute('data-wrap-pick') === style;
      setClass(btn, 'is-selected', on && mine);
      setClass(btn, 'is-suggested', !on && mine);
      setAttr(btn, 'aria-pressed', on && mine ? 'true' : 'false');
    });
    var name = qs(card, '.dm-cyg__name');
    if (name && name.dataset.wrapBaseName) {
      var wanted = on ? name.dataset.wrapBaseName + ' · ' + kit(style).label : name.dataset.wrapBaseName;
      if (name.textContent !== wanted) name.textContent = wanted;
    }
    syncSecond(card, on);
    syncInstant(card, on, style);
  }

  function watchCard(card) {
    if (!window.MutationObserver) return;
    // The builders re-mark is-active / aria-pressed on every update. Re-sync
    // after each change. Every write in the sync is conditional and the sync's
    // own records are discarded, so this settles in one pass and can never loop.
    var observer = new MutationObserver(function () {
      syncCygCard(card);
      observer.takeRecords(); // drop the records our own writes just queued
    });
    observer.observe(card, { attributes: true, subtree: true, attributeFilter: ['class', 'aria-pressed', 'data-addon-mode'] });
  }

  // Capture phase, registered before the builders' own document/root
  // listeners (this script is deferred ahead of them), so the card's data is
  // swapped before the builder reads the mode and recalculates totals.
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.dm-cyg__card--wrap-styles [data-dm-addon-mode]');
    if (!btn) return;
    var card = btn.closest('.dm-cyg__card');
    if (!card) return;
    var mode = btn.getAttribute('data-dm-addon-mode');
    if (mode === 'none') delete card.dataset.wrapStyle;
    else applyCygStyle(card, card.dataset.wrapStyle || state.style);
    window.setTimeout(function () { syncCygCard(card); }, 0);
  }, true);

  // Choose a colour, and switch the kit on if it was off.
  function pickStyle(card, style) {
    applyCygStyle(card, style);
    if ((card.dataset.addonMode || 'none') === 'none') {
      if (card.classList.contains('dm-wrap-instant')) card.dataset.addonMode = 'single';
      else if (card.classList.contains('dm-wrap-card')) setMode(card, 'single');
      else {
        var single = qs(card, '.dm-addon-mode [data-dm-addon-mode="single"]');
        if (single) single.click();
      }
    }
    syncCygCard(card);
    redrawBuilder();
  }

  // Swatch taps (the magnifier corner is handled by the zoom view below).
  document.addEventListener('click', function (event) {
    var pick = event.target && event.target.closest && event.target.closest('[data-wrap-pick]');
    if (!pick || event.target.closest('[data-wrap-zoom]')) return;
    var card = pick.closest('.dm-cyg__card');
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();
    pickStyle(card, pick.getAttribute('data-wrap-pick'));
  }, true);

  /* ---------------- zoom: a closer look at every colour ---------------- */
  // The magnifier on a swatch, or a tap on the card photo, opens one large
  // photo with the other colours underneath to flick between (arrows, swipe
  // or the small tabs). Tapping the photo magnifies it 2.4x around the finger;
  // "Choose" picks that colour on the card that opened it.

  var lb = null;
  var lbCard = null;
  var lbStyle = null;
  var lbOpener = null;

  function lbEl(sel) { return qs(lb, sel); }
  function buildLightbox() {
    lb = document.createElement('div');
    lb.className = 'dm-wrap-lb';
    lb.hidden = true;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Gift wrap kits');
    lb.innerHTML = '<div class="dm-wrap-lb__panel">' +
      '<div class="dm-wrap-lb__stage"><img class="dm-wrap-lb__img" alt="" width="1200" height="1200">' +
      '<span class="dm-wrap-lb__hint">Tap the photo to zoom in</span>' +
      '<button type="button" class="dm-wrap-lb__btn dm-wrap-lb__close" data-wrap-lb="close" aria-label="Close">&times;</button>' +
      '<button type="button" class="dm-wrap-lb__btn dm-wrap-lb__prev" data-wrap-lb="prev" aria-label="Previous colour">&#8249;</button>' +
      '<button type="button" class="dm-wrap-lb__btn dm-wrap-lb__next" data-wrap-lb="next" aria-label="Next colour">&#8250;</button></div>' +
      '<p class="dm-wrap-lb__title"><span class="dm-wrap-lb__name"></span><span class="dm-wrap-lb__price"></span></p>' +
      '<div class="dm-wrap-lb__tabs" style="--dm-wrap-n:' + SHOWN.length + '">' + SHOWN.map(function (style) {
        var k = kit(style);
        return '<button type="button" class="dm-wrap-lb__tab" data-wrap-lb-tab="' + esc(style) + '"><img src="' + esc(k.thumb) + '" alt="" width="48" height="48">' + esc(k.label) + '</button>';
      }).join('') + '</div>' +
      '<button type="button" class="dm-wrap-lb__choose" data-wrap-lb="choose"></button></div>';
    document.body.appendChild(lb);

    var stage = lbEl('.dm-wrap-lb__stage');
    var img = lbEl('.dm-wrap-lb__img');
    function aim(x, y) {
      var r = img.getBoundingClientRect();
      var px = Math.max(0, Math.min(100, (x - r.left) / r.width * 100));
      var py = Math.max(0, Math.min(100, (y - r.top) / r.height * 100));
      img.style.transformOrigin = px + '% ' + py + '%';
    }
    lb.addEventListener('click', function (event) {
      var t = event.target;
      var act = t.closest('[data-wrap-lb]');
      var tab = t.closest('[data-wrap-lb-tab]');
      if (t === lb || (act && act.getAttribute('data-wrap-lb') === 'close')) closeZoom();
      else if (act && act.getAttribute('data-wrap-lb') === 'prev') stepZoom(-1);
      else if (act && act.getAttribute('data-wrap-lb') === 'next') stepZoom(1);
      else if (act && act.getAttribute('data-wrap-lb') === 'choose') {
        if (lbCard) {
          pickStyle(lbCard, lbStyle);
          if (lbCard.classList.contains('dm-wrap-instant')) instantAdd(lbCard);
        }
        closeZoom();
      } else if (tab) showZoom(tab.getAttribute('data-wrap-lb-tab'));
      else if (t === img) {
        if (!stage.classList.contains('is-zoomed')) aim(event.clientX, event.clientY);
        stage.classList.toggle('is-zoomed');
      }
    });
    // Zoomed: the magnified spot follows the finger. Not zoomed: swipe for the next colour.
    var x0 = null;
    stage.addEventListener('touchstart', function (event) {
      x0 = event.touches.length === 1 ? event.touches[0].clientX : null;
    }, { passive: true });
    stage.addEventListener('touchmove', function (event) {
      if (!stage.classList.contains('is-zoomed') || event.touches.length !== 1) return;
      event.preventDefault();
      aim(event.touches[0].clientX, event.touches[0].clientY);
    }, { passive: false });
    stage.addEventListener('touchend', function (event) {
      if (x0 === null || stage.classList.contains('is-zoomed')) { x0 = null; return; }
      var dx = event.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 45) stepZoom(dx < 0 ? 1 : -1);
    }, { passive: true });
    stage.addEventListener('mousemove', function (event) {
      if (stage.classList.contains('is-zoomed')) aim(event.clientX, event.clientY);
    });
    document.addEventListener('keydown', function (event) {
      if (lb.hidden) return;
      if (event.key === 'Escape') closeZoom();
      else if (event.key === 'ArrowLeft') stepZoom(-1);
      else if (event.key === 'ArrowRight') stepZoom(1);
    });
  }

  function showZoom(style) {
    if (!KITS[style]) style = SHOWN[0];
    lbStyle = style;
    var k = kit(style);
    var stage = lbEl('.dm-wrap-lb__stage');
    stage.classList.remove('is-zoomed');
    var img = lbEl('.dm-wrap-lb__img');
    img.src = k.zoom || k.thumb;
    img.alt = k.name;
    lbEl('.dm-wrap-lb__name').textContent = k.name;
    lbEl('.dm-wrap-lb__price').textContent = money(k.price);
    qsa(lb, '[data-wrap-lb-tab]').forEach(function (tab) {
      var mine = tab.getAttribute('data-wrap-lb-tab') === style;
      setClass(tab, 'is-current', mine);
      setAttr(tab, 'aria-pressed', mine ? 'true' : 'false');
    });
    var choose = lbEl('.dm-wrap-lb__choose');
    if (lbCard && lbCard.classList.contains('dm-wrap-instant')) {
      var t = instantText(lbCard, style, true);
      choose.textContent = t.text;
      setClass(choose, 'is-chosen', t.done);
    } else {
      var chosen = lbCard && (lbCard.dataset.addonMode || 'none') !== 'none' && (lbCard.dataset.wrapStyle || state.style) === style;
      choose.textContent = chosen ? '\u2713 ' + k.label + ' chosen' : 'Choose ' + k.label + ' \u00b7 ' + money(k.price);
      setClass(choose, 'is-chosen', chosen);
    }
    // Warm the neighbours so flicking through is instant.
    [-1, 1].forEach(function (d) {
      var n = kit(SHOWN[(SHOWN.indexOf(style) + d + SHOWN.length) % SHOWN.length]);
      if (n.zoom) (new Image()).src = n.zoom;
    });
  }
  function stepZoom(d) {
    showZoom(SHOWN[(SHOWN.indexOf(lbStyle) + d + SHOWN.length) % SHOWN.length]);
  }
  function openZoom(card, style, opener) {
    if (!lb) buildLightbox();
    lbCard = card;
    lbOpener = opener;
    showZoom(style);
    lb.hidden = false;
    document.documentElement.classList.add('dm-wrap-lb-open');
    var close = lbEl('[data-wrap-lb="close"]');
    if (close) close.focus({ preventScroll: true });
  }
  function closeZoom() {
    if (!lb || lb.hidden) return;
    lb.hidden = true;
    document.documentElement.classList.remove('dm-wrap-lb-open');
    if (lbOpener && lbOpener.focus) lbOpener.focus({ preventScroll: true });
    lbCard = null;
    lbOpener = null;
  }

  // Window capture runs before every document and element listener, so the
  // theme's own single-photo zoom on the card thumbnail never opens as well.
  function zoomTarget(event) {
    var t = event.target;
    if (!t || !t.closest) return null;
    var card = t.closest('.dm-cyg__card--wrap-styles');
    if (!card) return null;
    var zoom = t.closest('[data-wrap-zoom]');
    if (zoom) return { card: card, style: zoom.getAttribute('data-wrap-zoom'), opener: zoom.closest('[data-wrap-pick]') };
    var thumb = t.closest('img.dm-cyg__thumb');
    if (thumb) return { card: card, style: card.dataset.wrapStyle || state.style, opener: thumb };
    return null;
  }
  window.addEventListener('click', function (event) {
    var z = zoomTarget(event);
    if (!z) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openZoom(z.card, z.style, z.opener);
  }, true);
  window.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var t = event.target;
    if (!t || !t.matches || !t.matches('.dm-cyg__card--wrap-styles img.dm-cyg__thumb')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var card = t.closest('.dm-cyg__card--wrap-styles');
    openZoom(card, card.dataset.wrapStyle || state.style, t);
  }, true);

  /* ---------------- B/C) checkbox rows -> the same card ---------------- */
  // The street-sign clones and the heart / diffuser builders offer the kit as
  // a tick-box row. That row is hidden and replaced with the exact Mr & Mrs
  // card (same markup, same buttons), then enhanced by the same code path as
  // above. The hidden tick box is kept in sync so the builder's own submit
  // code still adds the kit; the hooks below swap in the chosen colour.

  function cardHtml() {
    return '<div class="dm-cyg__card dm-wrap-card" role="group" data-addon-key="giftwrap" data-addon-mode="none" data-variant="' + classicId + '" data-price="' + CLASSIC.price + '" data-name="' + esc(CLASSIC.name) + '">' +
      '<div class="dm-cyg__inner"><div class="dm-cyg__media">' +
      '<img class="dm-cyg__thumb" src="' + esc(CLASSIC.thumb) + '" alt="' + esc(CLASSIC.name) + '" width="64" height="64" loading="lazy" data-dm-zoomable data-dm-zoom-src="' + esc(CLASSIC.zoom || CLASSIC.thumb) + '">' +
      '<div class="dm-cyg__body"><div class="dm-cyg__top"><span class="dm-cyg__name">Gift Wrap Kit</span><span class="dm-cyg__price">' + esc(money(CLASSIC.price)) + '</span></div>' +
      '<p class="dm-cyg__desc"></p></div></div>' +
      '<div class="dm-addon-mode" aria-label="Gift wrap kit">' +
      '<button class="dm-addon-mode__option is-active" type="button" data-dm-addon-mode="none" aria-pressed="true">No thanks</button>' +
      '<button class="dm-addon-mode__option" type="button" data-dm-addon-mode="single" aria-pressed="false">Add gift wrap kit</button>' +
      '</div></div></div>';
  }

  function isWrapCheckbox(input) {
    if (input.type !== 'checkbox' || input.dataset.wrapEnhanced) return false;
    if (/gift[-_]?wrap/i.test(input.name || '')) return true;
    // The football signs key their tick box on a data attribute instead of a name.
    for (var i = 0; i < input.attributes.length; i++) {
      if (/gift[-_]?wrap/i.test(input.attributes[i].name)) return true;
    }
    if (input.hasAttribute('data-heart-extra')) {
      var label = input.closest('label');
      return !!(label && /gift wrap kit/i.test(label.textContent || ''));
    }
    return false;
  }

  function setMode(card, mode) {
    if (card.dataset.addonMode !== mode) card.dataset.addonMode = mode;
    qsa(card, '.dm-addon-mode > [data-dm-addon-mode]').forEach(function (btn) {
      var active = btn.getAttribute('data-dm-addon-mode') === mode;
      setClass(btn, 'is-active', active);
      setAttr(btn, 'aria-pressed', active ? 'true' : 'false');
    });
    if (mode === 'none') delete card.dataset.wrapStyle;
    else if (!card.dataset.wrapStyle) card.dataset.wrapStyle = state.style;
    syncCygCard(card);
    var input = card.__dmInput;
    if (input) {
      var want = mode !== 'none';
      if (input.checked !== want) {
        input.checked = want;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function mountCard(input) {
    input.dataset.wrapEnhanced = 'true';
    // Hide the whole row (some builders wrap the label in a card with its own
    // quantity buttons), or the label, or as a last resort the parent.
    var host = input.closest('.daisy-street-options__choice') || input.closest('label') || input.parentNode;
    if (!host || !host.parentNode) return;
    var mount = document.createElement('div');
    mount.className = 'dm-wrap-mount';
    mount.innerHTML = cardHtml();
    var card = mount.firstChild;
    card.__dmInput = input;
    host.parentNode.insertBefore(mount, host.nextSibling);
    host.hidden = true;
    host.style.setProperty('display', 'none', 'important');
    enhanceCygCard(card);
    // If anything else unticks the box (a builder reset), the card follows.
    input.addEventListener('change', function () {
      if (!input.checked && (card.dataset.addonMode || 'none') !== 'none') setMode(card, 'none');
    });
    state.rows += 1;
  }

  // Mode clicks on the self-made cards (the builders only handle their own).
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.dm-wrap-card [data-dm-addon-mode]');
    if (!btn) return;
    var card = btn.closest('.dm-wrap-card');
    if (!card) return;
    event.preventDefault();
    setMode(card, btn.getAttribute('data-dm-addon-mode') || 'none');
  });

  /* ---------------- cart hooks ---------------- */

  function renameAddon(value) {
    if (typeof value !== 'string' || !/gift wrap kit/i.test(value)) return value;
    var label = kit(state.style).label;
    if (new RegExp(label, 'i').test(value)) return value;
    // "Add a Gift Wrap Kit" / "Add 2 Gift Wrap Kits" (builder labels) -> "Emerald Gift Wrap Kit(s)"
    return value.replace(/^add (an?|\d+) /i, '').replace(/gift wrap kit/i, label + ' Gift Wrap Kit');
  }

  function rewriteItems(items) {
    if (state.style === 'classic' || !KITS[state.style] || !state.rows) return items;
    var targetId = Number(kit(state.style).variantId);
    return items.map(function (item) {
      if (!item || !isClassicId(item.id)) return item;
      // The hooks stack (native sizes -> DaisyCartSubmit -> fetch), so a
      // second kit added by an earlier pass keeps its own colour.
      if (item.properties && item.properties['Add-on'] === SECOND_LABEL) return item;
      var copy = {};
      Object.keys(item).forEach(function (key) { copy[key] = item[key]; });
      if (Number(item.id) === twoPackId) copy.quantity = (Number(item.quantity) || 1) * 2;
      copy.id = targetId;
      if (item.properties && typeof item.properties === 'object') {
        var props = {};
        Object.keys(item.properties).forEach(function (key) { props[key] = renameAddon(item.properties[key]); });
        copy.properties = props;
      }
      return copy;
    });
  }

  function secondLine(primaryProps) {
    var props = { 'Add-on': SECOND_LABEL };
    if (primaryProps) {
      if (primaryProps['_Bundle ID']) props['_Bundle ID'] = primaryProps['_Bundle ID'];
      if (primaryProps['_Linked product']) props['_Linked product'] = primaryProps['_Linked product'];
    }
    return { id: kit(state.second).variantId, quantity: 1, properties: props };
  }

  function appendSecond(items) {
    if (!SECOND || !state.second || !KITS[state.second]) return items;
    if (items.some(function (i) { return i && i.properties && i.properties['Add-on'] === SECOND_LABEL; })) return items;
    var primary = null;
    items.forEach(function (i) { if (!primary && i && isKitId(i.id)) primary = i; });
    if (!primary) return items;
    return items.concat([secondLine(primary.properties)]);
  }

  function transformItems(items) {
    if (!Array.isArray(items)) return items;
    return appendSecond(rewriteItems(items));
  }

  function hookCartSubmit() {
    var lib = window.DaisyCartSubmit;
    if (!lib || lib.__dmWrapKits || typeof lib.create !== 'function') return;
    var create = lib.create;
    lib.create = function () {
      var api = create.apply(this, arguments);
      if (api && typeof api.add === 'function' && !api.__dmWrapKits) {
        var add = api.add;
        api.add = function (items, onSlow) { return add.call(api, transformItems(items), onSlow); };
        api.__dmWrapKits = true;
      }
      return api;
    };
    lib.__dmWrapKits = true;
  }

  function hookNativeSizes() {
    var lib = window.DaisyNativeStreetSizes;
    if (!lib || lib.__dmWrapKits) return;
    if (typeof lib.submit === 'function') {
      var submit = lib.submit;
      lib.submit = function (items) { return submit.call(lib, transformItems(items)); };
    }
    if (typeof lib.add === 'function') {
      var add = lib.add;
      lib.add = function (items, config) { return add.call(lib, transformItems(items), config); };
    }
    lib.__dmWrapKits = true;
  }

  // Form-style single-item posts (heart / diffuser builders post one line at a
  // time): rewrite the kit line in place and, when a second kit was chosen,
  // return the extra line to post straight after it.
  function rewriteForm(body) {
    var id = Number(body.get('id'));
    if (!isKitId(id)) return null;
    if (isClassicId(id) && state.rows && state.style !== 'classic' && KITS[state.style]) {
      if (id === twoPackId) body.set('quantity', String((Number(body.get('quantity')) || 1) * 2));
      body.set('id', String(kit(state.style).variantId));
      if (body.has('properties[Add-on]')) body.set('properties[Add-on]', renameAddon(body.get('properties[Add-on]')));
    }
    if (!SECOND || !state.second || !KITS[state.second]) return null;
    var props = {};
    if (body.has('properties[_Bundle ID]')) props['_Bundle ID'] = body.get('properties[_Bundle ID]');
    if (body.has('properties[_Linked product]')) props['_Linked product'] = body.get('properties[_Linked product]');
    return secondLine(props);
  }

  function hookFetch() {
    if (!window.fetch || window.fetch.__dmWrapKits) return;
    var original = window.fetch;
    var wrapped = function (input, init) {
      var extra = null;
      try {
        var url = typeof input === 'string' ? input : ((input && input.url) || '');
        if (/\/cart\/add(\.js)?(\?|$)/.test(url) && init && init.body) {
          var body = init.body;
          if (typeof body === 'string' && body.trim().charAt(0) === '{') {
            var data = JSON.parse(body);
            if (Array.isArray(data.items)) data.items = transformItems(data.items);
            else if (isKitId(data.id)) { var t = transformItems([data]); data = t[0]; if (t[1]) extra = t[1]; }
            init = Object.assign({}, init, { body: JSON.stringify(data) });
          } else if (typeof body === 'string' && /(^|&)id=/.test(body)) {
            var params = new URLSearchParams(body);
            extra = rewriteForm(params);
            init = Object.assign({}, init, { body: params.toString() });
          } else if ((typeof FormData !== 'undefined' && body instanceof FormData) || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
            extra = rewriteForm(body);
          }
        }
      } catch (e) { extra = null; /* never block a cart post */ }
      var request = original.call(window, input, init);
      if (!extra) return request;
      return request.then(function (response) {
        if (!response || !response.ok) return response;
        return original.call(window, '/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [extra] })
        }).then(function () { return response; }, function () { return response; });
      });
    };
    wrapped.__dmWrapKits = true;
    window.fetch = wrapped;
  }

  /* ---------------- D) one-tap card (simple product pages) ---------------- */
  // snippets/dm-simple-addons.liquid (the tea light holders) has no builder to
  // add the kit with the product, so its kit card adds straight to the basket.
  // It becomes the same card: swatches, zoom, and one button that adds the
  // chosen colour (Ivory if none is picked, as on every other page). After one
  // kit is in, the next colour is offered at the half-price second-kit rate.

  function instantMarkup() {
    return '<div class="dm-cyg__card dm-cyg__card--wrap-styles dm-wrap-instant" role="group" aria-label="Gift wrap kit" data-wrap-enhanced="true" data-addon-mode="none" data-variant="' + classicId + '" data-price="' + CLASSIC.price + '">' +
      '<div class="dm-cyg__media"><img class="dm-cyg__thumb" src="' + esc(CLASSIC.thumb) + '" alt="' + esc(CLASSIC.name) + '" width="64" height="64" loading="lazy" data-dm-zoomable data-dm-zoom-src="' + esc(CLASSIC.zoom || CLASSIC.thumb) + '" tabindex="0" role="button" aria-label="Look closer at the gift wrap colours">' +
      '<div class="dm-cyg__body"><div class="dm-cyg__top"><span class="dm-cyg__name" data-wrap-base-name="Gift Wrap Kit">Gift Wrap Kit</span><span class="dm-cyg__price">' + esc(money(CLASSIC.price)) + '</span></div>' +
      '<p class="dm-cyg__desc">' + esc(CFG.copy || '') + '</p></div></div>' +
      swatchesHtml() +
      '<button type="button" class="dm-sa-btn dm-wrap-instant__add" data-wrap-instant-add></button></div>';
  }
  function mountInstant(holder) {
    if (holder.dataset.wrapMounted) return;
    holder.dataset.wrapMounted = 'true';
    holder.className = 'dm-wrap-mount';
    holder.innerHTML = instantMarkup();
    var card = holder.firstChild;
    applyCygStyle(card, SUGGESTED || 'classic', true);
    syncCygCard(card);
  }
  function instantText(card, style, named) {
    var k = kit(style);
    var added = card.dataset.wrapAdded ? card.dataset.wrapAdded.split(',') : [];
    if (added.indexOf(style) !== -1) return { text: '\u2713 ' + k.label + ' kit added to basket', done: true };
    if (added.length === 1 && SECOND) return { text: 'Add ' + k.label + ' too \u00b7 ' + money(halfPrice(k)) + ' (half price)', done: false };
    return { text: 'Add ' + (named ? k.label + ' ' : '') + 'gift wrap \u00b7 ' + money(k.price), done: false };
  }
  function syncInstant(card, on, style) {
    var btn = qs(card, '[data-wrap-instant-add]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    var t = instantText(card, on ? style : 'classic', on);
    if (btn.textContent !== t.text) btn.textContent = t.text;
    setClass(btn, 'dm-sa-added', t.done);
    if (btn.disabled !== t.done) btn.disabled = t.done;
  }
  function instantAdd(card) {
    var on = (card.dataset.addonMode || 'none') !== 'none';
    var style = on ? (card.dataset.wrapStyle || state.style) : 'classic';
    var btn = qs(card, '[data-wrap-instant-add]');
    if (instantText(card, style, on).done || (btn && btn.getAttribute('aria-busy') === 'true')) return;
    var k = kit(style);
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; btn.textContent = 'Adding\u2026'; }
    var data = new FormData();
    data.append('id', String(k.variantId));
    data.append('quantity', '1');
    data.append('properties[Add-on]', k.label + ' Gift Wrap Kit');
    function finish(ok) {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
      if (ok) card.dataset.wrapAdded = (card.dataset.wrapAdded ? card.dataset.wrapAdded + ',' : '') + style;
      syncCygCard(card);
      if (ok) document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    window.fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin', body: data })
      .then(function (r) { finish(r.ok); }, function () { finish(false); });
  }
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('[data-wrap-instant-add]');
    var card = btn && btn.closest('.dm-wrap-instant');
    if (!card) return;
    event.preventDefault();
    instantAdd(card);
  });

  /* ---------------- boot ---------------- */

  function scan() {
    qsa(document, '[data-dm-wrap-instant]').forEach(mountInstant);
    qsa(document, '.dm-cyg__card').forEach(enhanceCygCard);
    qsa(document, 'input[type="checkbox"]').forEach(function (input) { if (isWrapCheckbox(input)) mountCard(input); });
  }

  function init() {
    hookCartSubmit();
    hookNativeSizes();
    hookFetch();
    scan();
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () { pending = null; scan(); }, 40);
      }).observe(document.body, { childList: true, subtree: true });
    }
    window.DaisyWrapKits = { kits: KITS, order: ORDER, suggested: SUGGESTED, state: state, transformItems: transformItems };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
